import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getUserRole, isPortalUser } from '../utils/userRole';
import { translateAcademyLoginError } from '../utils/portalAuthMessages';
import './LoginPage.css';

const VK_FORGOT_URL = 'https://vk.com/englishalex';

/** Safe in-app path from login redirect (state or ?redirect=). */
function resolvePostLoginPath(location, role) {
    const from = location.state?.from;
    if (from?.pathname && from.pathname.startsWith('/') && !from.pathname.startsWith('//')) {
        return `${from.pathname}${from.search || ''}`;
    }
    const redirect = new URLSearchParams(location.search).get('redirect');
    if (redirect?.startsWith('/') && !redirect.startsWith('//')) {
        return redirect;
    }
    if (role === 'teacher') return '/teacher';
    if (role === 'portal') return '/portal';
    return '/student';
}

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [errorCode, setErrorCode] = useState(null);
    const [deactivatedInfo, setDeactivatedInfo] = useState(false);
    const [showForgotTip, setShowForgotTip] = useState(false);
    const { login, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const forgotWrapRef = useRef(null);

    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const goAfterLogin = useCallback((role) => {
        navigate(resolvePostLoginPath(location, role), { replace: true });
    }, [location, navigate]);

    useEffect(() => {
        if (user) {
            if (isPortalUser(user)) {
                goAfterLogin('portal');
                return;
            }
            const role = getUserRole(user) || 'student';
            goAfterLogin(role);
        }
    }, [user, goAfterLogin]);

    useEffect(() => {
        if (!showForgotTip) return undefined;

        const onPointerDown = (e) => {
            if (forgotWrapRef.current && !forgotWrapRef.current.contains(e.target)) {
                setShowForgotTip(false);
            }
        };

        document.addEventListener('pointerdown', onPointerDown);
        return () => document.removeEventListener('pointerdown', onPointerDown);
    }, [showForgotTip]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoggingIn(true);
        setError('');
        setErrorCode(null);
        setDeactivatedInfo(false);
        try {
            const result = await login(username, password);
            if (result.success) {
                goAfterLogin(result.role === 'teacher' ? 'teacher' : 'student');
            } else if (result.code === 'ACCOUNT_DEACTIVATED') {
                setDeactivatedInfo(true);
                setError(translateAcademyLoginError(result.message, result.code));
            } else {
                setErrorCode(result.code || null);
                setError(translateAcademyLoginError(result.message, result.code));
            }
        } catch {
            setError('Не удалось войти. Попробуйте ещё раз.');
        } finally {
            setIsLoggingIn(false);
        }
    };

    return (
        <div className="academy-login-page">
            <div className="academy-login-mesh" aria-hidden>
                <div className="academy-login-orb academy-login-orb--1" />
                <div className="academy-login-orb academy-login-orb--2" />
            </div>

            <div className="academy-login-card animate-fade-in">
                <div className="academy-login-header">
                    <Link to="/" className="academy-login-back">
                        ← На главную
                    </Link>
                    <div className="academy-login-logo font-header">
                        <span aria-hidden>🌿</span>
                        <span>АлексКласс</span>
                    </div>
                    <h1 className="academy-login-title font-header">Вход</h1>
                    <p className="academy-login-subtitle">
                        Войдите в личный кабинет ученика или преподавателя.
                    </p>
                </div>

                {deactivatedInfo && (
                    <div className="academy-login-deactivated" role="alert">
                        <div className="academy-login-deactivated-icon" aria-hidden>
                            ⏸
                        </div>
                        <h2 className="academy-login-deactivated-title">Аккаунт деактивирован</h2>
                        <p className="academy-login-deactivated-text">{error}</p>
                    </div>
                )}
                {error && !deactivatedInfo && (
                    <div className="academy-login-error" role="alert">
                        {error}
                        {errorCode === 'USE_PORTAL_LOGIN' && (
                            <>
                                {' '}
                                <Link to="/portal/login">Перейти ко входу в Портал</Link>
                            </>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="academy-login-form">
                    <div className="academy-login-field">
                        <label htmlFor="username" className="academy-login-label">
                            Логин
                        </label>
                        <input
                            id="username"
                            type="text"
                            className="academy-login-input"
                            placeholder="Ваш логин"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            autoComplete="username"
                            required
                        />
                    </div>
                    <div className="academy-login-field">
                        <div className="academy-login-label-row">
                            <label htmlFor="password" className="academy-login-label">
                                Пароль
                            </label>
                            <div className="academy-login-forgot-wrap" ref={forgotWrapRef}>
                                <button
                                    type="button"
                                    className="academy-login-forgot-btn"
                                    aria-expanded={showForgotTip}
                                    aria-describedby={showForgotTip ? 'login-forgot-tip' : undefined}
                                    onClick={() => setShowForgotTip((v) => !v)}
                                >
                                    Забыли?
                                </button>
                                {showForgotTip && (
                                    <div id="login-forgot-tip" className="academy-login-forgot-tip" role="tooltip">
                                        Свяжитесь с автором{' '}
                                        <a href={VK_FORGOT_URL} target="_blank" rel="noopener noreferrer">
                                            {VK_FORGOT_URL}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                        <input
                            id="password"
                            type="password"
                            className="academy-login-input"
                            placeholder="Введите пароль"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                    </div>
                    <button type="submit" className="academy-login-submit" disabled={isLoggingIn}>
                        {isLoggingIn ? 'Входим…' : 'Войти'}
                    </button>
                </form>

                <div className="academy-login-footer">
                    <p>
                        Нужен Портал с ИИ-проверками?{' '}
                        <Link to="/portal/register">Создать аккаунт</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
